import React from "react";
import { Download } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { SOCIAL_ITEMS } from "@/mock/data";
import { TextField, TextareaField, FileField, YesNo, Criteria, QuestionCard } from "@/components/fields/Field";

// ===================== STEP 7 : Business plan =====================
const BP_BOXES = [
  { key: "0", title: "सेवा विस्तार" },
  { key: "1", title: "जनजागृती" },
  { key: "2", title: "आर्थिक व्यवहार्यता" },
  { key: "3", title: "व्यवसाय नियोजन" },
];

export const bpWordCount = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;

export function Step7({ errors = {} }) {
  const { application, removeFields } = useApp();
  const d = application.data;
  return (
    <div className="animate-fade-up">
      <Criteria label="सूचना:">प्रत्येक बाबीसाठी "आहे / नाही" निवडा. "आहे" निवडल्यास किमान <strong>२५ शब्दांचे</strong> वर्णन लिहिणे आवश्यक आहे.</Criteria>
      <div className="mb-4 rounded-lg border-l-4 border-[#E0A106] bg-[#fdf7e5] p-4 text-[13.5px] leading-relaxed text-[#5c4a1c]">
        <strong>सूचना:</strong> सेवा विस्तार अंतर्गत अर्जदाराने केंद्रामार्फत कोणकोणत्या अतिरिक्त सेवा देणार आहे हे नमूद करावे, जसे की बँकिंग, विमा, पासपोर्ट सेवा, आधार अपडेट, विविध दाखले, PAN इ., तसेच भविष्यात या सेवांचा आवाका कसा वाढवणार याचे नियोजन स्पष्ट करावे. जनजागृती या मुद्द्यामध्ये केंद्राची माहिती स्थानिक नागरिकांपर्यंत पोहोचवण्यासाठी कोणती साधने वापरणार (बॅनर, ग्रामसभा, सोशल मीडिया इ.) आणि विशेषतः ग्रामीण व दुर्गम भागातील नागरिकांपर्यंत पोहोचण्यासाठी कोणती विशेष योजना राबवणार हे नमूद करावे. आर्थिक व्यवहार्यता या मुद्द्याखाली केंद्र सुरू करण्यासाठी लागणारा प्रारंभिक खर्च व त्याचे नियोजन, अपेक्षित महसूल व खर्च-उत्पन्नाचा ताळमेळ, तसेच केंद्र दीर्घकाळ आर्थिकदृष्ट्या कसे टिकेल याबाबत स्पष्टता द्यावी. शेवटी व्यवसाय नियोजन अंतर्गत पुढील 1-3 वर्षांसाठीचा कृती आराखडा, कर्मचारी नियोजन व वाढीचे टप्पे, तसेच इतर केंद्रांच्या तुलनेत आपले केंद्र कशाप्रकारे वेगळे व उपयुक्त ठरेल हे विशद करावे.
      </div>
      <div>
        {BP_BOXES.map((box, i) => {
          const planned = d[`bp_plan_${i}`] === "yes";
          const count = bpWordCount(d[`bp_text_${i}`]);
          const invalid = !!errors[`bp_text_${i}`];
          return (
            <QuestionCard key={box.key} index={i} number={i + 1} title={box.title}
              headerRight={
                <div className="flex items-center gap-3">
                  <span className="text-[13.5px] text-slate-600">नियोजन केलेले</span>
                  <YesNo name={`bp_plan_${i}`} yesLabel="आहे" noLabel="नाही" onToggle={(v) => { if (v === "no") removeFields((k) => k === `bp_text_${i}`); }} />
                </div>
              }>
              {planned ? (
                <div>
                  <TextareaField label={`${box.title} — तपशील (किमान २५ शब्द)`} name={`bp_text_${i}`} rows={4} required errors={errors} />
                  <p className={`mt-1 text-[12.5px] ${count >= 25 ? "text-[#3a6b1c]" : invalid ? "text-[#dc2626]" : "text-slate-500"}`}>
                    शब्द संख्या: <strong>{count}</strong> / किमान २५ {count >= 25 ? "✓" : ""}
                  </p>
                </div>
              ) : (
                <p className="text-[12.5px] text-slate-400">"आहे" निवडल्यास किमान २५ शब्दांचे वर्णन आवश्यक.</p>
              )}
            </QuestionCard>
          );
        })}

        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
          <p className="mb-2 text-[14px] font-semibold text-slate-700">व्यवसाय आराखडा दस्तऐवज अपलोड (ऐच्छिक — इच्छित असल्यास)</p>
          <FileField label="व्यवसाय आराखडा (PDF)" name="bp_pdf" note="PDF/JPG/PNG, कमाल आकार 1MB — ऐच्छिक" errors={errors} />
        </div>
      </div>
    </div>
  );
}

// ===================== STEP 8 : Social inclusion =====================
export function Step8({ errors = {} }) {
  const { application, removeFields, setField } = useApp();
  const female = application.data.gender === "स्त्री" ? "yes" : "no";
  React.useEffect(() => {
    if (application.data.si_female !== female) setField("si_female", female);
  }, [female, application.data.si_female, setField]);

  const roPill = (active) =>
    `flex items-center gap-2 rounded-full border px-4 py-1.5 text-[14px] cursor-default select-none ${
      active ? "border-[#1b5e3f] bg-[#1b5e3f] text-white shadow-sm" : "border-slate-300 bg-white text-slate-400"
    }`;

  return (
    <div className="animate-fade-up">
      <p className="mb-4 text-[13.5px] text-slate-600">
        लागू असलेल्या प्राधान्यक्रमासाठी "आहे" निवडा — निवडल्यास पुराव्याची कागदपत्रे अपलोड करणे <strong>आवश्यक</strong> आहे.
      </p>
      <div>
        {/* महिला — Step 1 मधील लिंगानुसार आपोआप निश्चित, बदलता येत नाही, कागदपत्र आवश्यक नाही */}
        <QuestionCard index={0} number={1} title="महिला" testid="step8-female-item"
          note="Step 1 मधील लिंगानुसार आपोआप निश्चित — बदलता येणार नाही, कागदपत्र आवश्यक नाही.">
          <div className="flex flex-wrap gap-3" data-testid="step8-female-yesno">
            <span className={roPill(female === "yes")}>
              <span className={`h-3 w-3 rounded-full border-2 ${female === "yes" ? "border-white bg-white" : "border-slate-300"}`} />
              होय
            </span>
            <span className={roPill(female === "no")}>
              <span className={`h-3 w-3 rounded-full border-2 ${female === "no" ? "border-white bg-white" : "border-slate-300"}`} />
              नाही
            </span>
          </div>
        </QuestionCard>

        {SOCIAL_ITEMS.map((item, i) => {
          const yes = application.data[item.key] === "yes";
          return (
            <QuestionCard key={item.key} index={i + 1} number={i + 2} title={item.label} note={item.note}>
              <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[auto_1fr]">
                <YesNo name={item.key} onToggle={(v) => { if (v === "no") removeFields((k) => k === `${item.key}_file` || k === `${item.key}_details`); }} />
                <div className={yes ? "" : "opacity-50 pointer-events-none"}>
                  <FileField label="पुराव्याची कागदपत्रे" name={`${item.key}_file`} required={yes} errors={errors} compact />
                </div>
              </div>
            </QuestionCard>
          );
        })}
      </div>
    </div>
  );
}

// ===================== STEP 9 : Character verification =====================
export function Step9({ errors = {} }) {
  const { application } = useApp();
  const d = application.data;
  const showAffidavit = d.cv_fraud === "no" || d.cv_blacklist === "no";
  return (
    <div className="animate-fade-up">
      <div>
        <QuestionCard index={0} number={1}
          title="पोलिसांचे चारित्र्य पडताळणी प्रमाणपत्र" required
          note="(दि. ०९/०७/२०२६ नंतरचे पोलिसांचे चारित्र्य पडताळणी प्रमाणपत्र जोडावे. अर्ज भरतेवेळी प्रमाणपत्र न मिळाल्यास अर्जासोबत टोकन जोडणे आवश्यक परंतु मुलाखातीवेळी मूळ प्रमाणपत्र जमा करणे अनिवार्य राहील)">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-[13px] font-semibold text-slate-700">पोलीस ठाण्याचे नाव</label>
              <CVCell name="cv_station" errors={errors} />
            </div>
            <div>
              <label className="mb-1 block text-[13px] font-semibold text-slate-700">क्रमांक</label>
              <CVCell name="cv_number" errors={errors} />
            </div>
            <div>
              <label className="mb-1 block text-[13px] font-semibold text-slate-700">दिनांक</label>
              <CVCell name="cv_date" type="date" errors={errors} />
            </div>
          </div>
          <div className="mt-3">
            <FileField label="प्रमाणपत्र / टोकन अपलोड करा" name="cv_file" compact errors={errors} />
          </div>
        </QuestionCard>
        <QuestionCard index={1} number={2}
          title="अर्जदाराचा कोणत्याही आर्थिक गैरव्यवहारामध्ये समावेश होता का? व त्या बाबतचा कोणताही गुन्हा प्रलंबित आहे का?" required>
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[auto_1fr]">
            <YesNo name="cv_fraud" />
            <TextareaField label="आहे असल्यास त्याचा तपशील" name="cv_fraud_details" rows={2} />
          </div>
        </QuestionCard>
        <QuestionCard index={2} number={3}
          title="अर्जदाराचा महाराष्ट्र शासनाच्या कोणत्याही विभागाने अथवा प्रशासनाने कोणत्याही कारणास्तव काळ्या यादीत समावेश केला आहे का?" required>
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[auto_1fr]">
            <YesNo name="cv_blacklist" />
            <TextareaField label="आहे असल्यास त्याचा तपशील" name="cv_blacklist_details" rows={2} />
          </div>
        </QuestionCard>

        {showAffidavit && (
          <div className="mb-5 rounded-xl border-l-4 border-[#E0A106] bg-[#fdf7e5] p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-[14px] font-semibold text-[#5c4a1c]">
                फसवणूक, आर्थिक गैरव्यवहार किंवा शासकीय काळ्या यादीत नसणे प्रतिज्ञापत्र <span className="text-[#E0A106]">*</span>
              </p>
              <a
                href="/affidavit-format.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-full bg-[#1b5e3f] px-3.5 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#144d33]"
              >
                <Download className="h-3.5 w-3.5" /> नमुना डाउनलोड करा
              </a>
            </div>
            <p className="mb-3 text-[12.5px] text-[#7a6a45]">वरील नमुन्यानुसार दंडाधिकारी यांच्यासमोर प्रतिज्ञापत्र तयार करून, स्वाक्षरी करून येथे अपलोड करा.</p>
            <FileField label="प्रतिज्ञापत्र अपलोड करा" name="cv_affidavit_file" required errors={errors} compact />
          </div>
        )}
      </div>
    </div>
  );
}

function CVCell({ name, type = "text", errors = {} }) {
  const { application, setField } = useApp();
  const value = application.data[name] ?? "";
  const invalid = !!errors[name];
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => setField(name, e.target.value)}
      className={`w-full rounded-md border px-2.5 py-1.5 text-[13px] outline-none focus:border-[#1b5e3f] focus:ring-1 focus:ring-[#1b5e3f]/20 ${invalid ? "border-[#dc2626]" : "border-slate-300"}`}
    />
  );
}
